import React, { useCallback, useEffect, useRef, useState } from "react";
import { Outlet, useParams, useLocation } from "react-router-dom";
import HeaderController from "../../components/Headers/HeaderController";
import "./MyAccountLayout.css";
import MyAccountAsideBar from "./components/MyAccountAsideBar/MyAccountAsideBar";
import Footer from "../../components/Footer/Footer";
import * as translator from "../../utils/translator";
import * as http from "../../utils/http";
import LoadingModal from "../../components/Modals/LoadingModal/LoadingModal";
import ServerError from "../../components/ServerError/ServerError";
import MyAccountThanksForUpdateModal from "../../components/Modals/MyAccountThanksForUpdateModal/MyAccountThanksForUpdateModal";
import MyAccountCancelSubscriptionModal from "../../components/Modals/MyAccountCancelSubscriptionModal/MyAccountCancelSubscriptionModal";
import StickyNotification from "../../components/StickyNotification/StickyNotification";
import HeaderCMS from "../../components/Headers/HeaderCMS/HeaderCMS";
import MyAccountSkipSubscriptionModal from "../../components/Modals/MyAccountSkipSubscriptionModal/MyAccountSkipSubscriptionModal";
import MyAccountChangeSubscriptionMedicationModal from "../../components/Modals/MyAccountChangeSubscriptionMedicationModal/MyAccountChangeSubscriptionMedicationModal";
import PharmacySelectionConfirmationModal from "../../components/Modals/PharmacySelectionConfirmationModal/PharmacySelectionConfirmationModal";
import PharmacyRequestForm from "../../components/PharmacyRequestForm/PharmacyRequestForm";
import PharmacyRequestNotificationModal from "../../components/Modals/PharmacyRequestNotificationModal/PharmacyRequestNotificationModal";
import PrescriptionRedemptionAlternativeOfferModal from "../../components/Modals/AlternativeOfferModal/PrescriptionRedemptionAlternativeOfferModal";
import AlternativeOfferModal from "../../components/Modals/AlternativeOfferModal/AlternativeOfferModal";
import DoctorAlternativeOfferModal from "../../components/Modals/AlternativeOfferModal/DoctorAlternativeOfferModal";
import { useCartContext } from "../../customHooks/useCart";
import { MainTracker } from "../../PixelTrackers/MainTracker";
import MyAccountStoreWalkMe from "./components/MyAccountStore/components/MyAccountStoreWalkMe";
import { testLabStoreConfig } from "./components/MyAccountStore/stores/testLabStoreConfig";

type AsideBarData = {
  id: number;
  firstName: string;
  lastName: string;
  ordersProducts: {
    product_id: number;
    title: string;
    localeID?: string;
  }[];
  ordersStatuses: string[];
  email: string;
  hasPassword?: boolean; // absent on older api responses, page then shows the normal form
  uuid: string;
  locale: {
    name: string;
    language_id: string;
    country_id: string;
  };
  orderPrescriptionDetailsPendingCustomerCount: number;
  orderConversationNotificationsCount: number;
  subscriptionCount: number;
  activeOrdersCount: number;
  hasActiveCarePlusSubscription?: boolean;
  carePlusSubscriptionHashId?: string | null;
  carePlusOrderHashIds?: string[];
  carePlusHasActionRequired?: boolean;
  /** Suppresses the app-install placements for customers who already have the app. */
  isPairedToMobileApp?: boolean;
  /** Placement ids the api allows. Undefined keeps them all hidden. */
  appInstallPlacements?: string[];
}

const MyAccountLayout = () => {
  const { country } = useParams();
  const location = useLocation();
  const [error, setError] = useState<any>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeMenuLinkId, setActiveMenuLinkId] = useState<string>("my-orders");
  // Mobile side-menu accordion state, lifted here so the store WalkMe can open
  // the menu before it highlights the "Testzentrum" item (a collapsed menu
  // otherwise clips the anchor at height:0 and mis-places the highlight).
  const [asideMenuOpen, setAsideMenuOpen] = useState<boolean>(false);
  // True while the store WalkMe is showing — reveals the target item's icon in
  // the side menu (desktop) for the duration of the walkthrough.
  const [storeWalkMeActive, setStoreWalkMeActive] = useState<boolean>(false);
  const [refreshData, setRefreshData] = useState<boolean>(false);
  const [asideBarData, setAsideBarData] = useState<AsideBarData | null>(null);
  const [currentGlobalModalName, setCurrentGlobalModalName] = useState<string | null | Object>(null);
  const [stickyNotificationDetails, setStickyNotificationDetails] = useState({
    show: false,
    title: "",
    content: "",
    level: "",
    showInfoIcon: true,
    refresh: false,
    backgroundColor: null,

  });

  const openAsideMenu = useCallback(() => setAsideMenuOpen(true), []);
  const closeAsideMenu = useCallback(() => setAsideMenuOpen(false), []);
  const handleStoreWalkMeActiveChange = useCallback(
    (active: boolean) => setStoreWalkMeActive(active),
    [],
  );

  const { saveCart, setCartCurrency} = useCartContext();
  const isDataAlreadyFetched = useRef(false);

  const renderModal = useCallback( (modalData: any) => {


    if (!asideBarData) {
      return <></>;
    }


    let modalName
    modalData.type ? modalName = modalData.type : modalName = modalData
    switch (modalName) {
      case "CANCEL_SUBSCRIPTION":
        return (
          <MyAccountCancelSubscriptionModal
            country={country}
            refreshData={refreshData}
            setRefreshData={setRefreshData}
            language={asideBarData.locale.language_id}
            setStickyNotificationDetails={setStickyNotificationDetails}
            closeModal={() => setCurrentGlobalModalName(null)}
            setCurrentGlobalModalName={setCurrentGlobalModalName}
            allSubscriptionData={modalData.subscriptionData}
          />
        );
      case "THANKS_FOR_UPDATE":
        return <MyAccountThanksForUpdateModal closeModal={() => setCurrentGlobalModalName(null)} />;

      case "PHARMACY_SELECTION_CONFIRMATION":
        return (
          <PharmacySelectionConfirmationModal
            closeModal={modalData.closeModal}
            onSubmit={modalData.onSubmit}
            selectedPharmacy={modalData.selectedPharmacy}
          />
        );
      
      case "PHARMACY_REQUEST":
        return (
          <PharmacyRequestForm
            closeModal={() => setCurrentGlobalModalName(null)}
            language={asideBarData.locale.language_id}
            showSuccessMessage={modalData.showSuccessMessage}
            setStickyNotificationDetails={setStickyNotificationDetails}
            orderHashID={modalData.orderHashID}
          />
        );
      
      case "PHARMACY_REQUEST_SUCCESS_NOTIFICATION":
        return (
          <PharmacyRequestNotificationModal
            closeModal={() => setCurrentGlobalModalName(null)}
            language={asideBarData.locale.language_id}
            pharmacyInfo={modalData.pharmacyInfo}
            disclaimerType={modalData.disclaimerType}
            setSearchQuery={modalData.setSearchQuery}
          />
        )

      case "SKIP_SUBSCRIPTION":

        return (
          <MyAccountSkipSubscriptionModal
            closeModal={() => setCurrentGlobalModalName(null)}
            allSubscriptionData={modalData.subscriptionData}
            modalData={modalData.data}
            setStickyNotificationDetails={setStickyNotificationDetails}
            setRefreshData={setRefreshData}
            language={asideBarData.locale.language_id}
          />
        );
      case "CHANGE_SUBSCRIPTION_MEDICATION":
        return (
          <MyAccountChangeSubscriptionMedicationModal
            setCurrentGlobalModalName={setCurrentGlobalModalName}
            allSubscriptionData={modalData.subscriptionData}
            modalData={modalData.data}
            setStickyNotificationDetails={setStickyNotificationDetails}
            setRefreshData={setRefreshData}
            refreshData={refreshData}
            language={asideBarData.locale.language_id}
            country={country}
          />
        );
      case "EXTERNAL_PHARMACY_ALTERNATIVE_OFFER":
        return (
          <PrescriptionRedemptionAlternativeOfferModal
            orderHashID={modalData.orderHashID}
            language={asideBarData.locale.language_id}
            setStickyNotificationDetails={setStickyNotificationDetails}
            onAlternativeOfferMenuTriggerReject={modalData.onAlternativeOfferMenuTriggerReject}
            onAlternativeOfferMenuTriggerAccept={modalData.onAlternativeOfferMenuTriggerAccept}
            closeModal={() => setCurrentGlobalModalName(null)}
            orderProducts={modalData.orderProducts}
            pharmacyAlternativeOffer={modalData.pharmacyAlternativeOffer}
          />
        );
      case "ALTERNATIVES_FOR_OUT_OF_STOCK":
        return (
          <AlternativeOfferModal
            closeModal={() => setCurrentGlobalModalName(null)}
            orderProducts={modalData.orderProducts}
            setRefreshData={setRefreshData}
            setStickyNotificationDetails={setStickyNotificationDetails}
          />
        );
      case "DOCTOR_ALTERNATIVE_OFFER":
        return (
          <DoctorAlternativeOfferModal
            closeModal={() => setCurrentGlobalModalName(null)}
            orderProducts={modalData.orderProducts}
            doctorAlternativeSuggestions={modalData.doctorAlternativeSuggestions}
            orderBundle={modalData.orderBundle}
            setRefreshData={setRefreshData}
            setStickyNotificationDetails={setStickyNotificationDetails}
          />
        );
      default:
        return null; // Return null instead of empty fragment
    }
  }, [currentGlobalModalName, asideBarData, refreshData]);

  useEffect(() => {
    MainTracker.track("pv", "Funnel My Account View Page", {
      current_url: window.location.href,
      subscription_status: asideBarData?.hasActiveCarePlusSubscription ? "active" : asideBarData ? "none" : null,
    });

    if (isDataAlreadyFetched.current) return;
    isDataAlreadyFetched.current = true;

    setLoading(true);
    http
      .getFromServer(`${country}/account/asidebar`, [http.MIDDLEWARES.CUSTOMER_ID, http.MIDDLEWARES.CART_ORDER])
      .then((response) => {

        if(response.data.cartData) {
          saveCart({...response.data.cartData, timekitBooking: response.data.cartData.timekitBooking || null});
          if (response.data.cartCurrency) {
            setCartCurrency(response.data.cartCurrency);
          }

          delete response.data.cartData;
        }

        if (typeof response.data.isPairedToMobileApp !== "boolean") {
          window.clientLogger.warn("Account asidebar response has no boolean isPairedToMobileApp, app-install banners stay hidden", { isPairedToMobileApp: response.data.isPairedToMobileApp });
        }
        setAsideBarData(response.data);
      })
      .catch((error) => {
        window.clientLogger.error("Error in MyAccountLayout: ", error);
        setError(500);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [location.pathname]);

  if (error && !loading) {
    return (<>
      <HeaderController
        language={country ?? "de"}
        stage="MyAccount"
        showSteps={false}
      />
      <ServerError status={error} />
    </>)
  }


  return (
    <>
      {loading && <LoadingModal />}

      <StickyNotification
        showNotification={stickyNotificationDetails.show}
        title={stickyNotificationDetails.title}
        onClick={() => setStickyNotificationDetails((currentDetails: any) => { return { ...currentDetails, show: false } })}
        content={stickyNotificationDetails.content}
        level={stickyNotificationDetails.level}
        showInfoIcon={stickyNotificationDetails.showInfoIcon}
        refresh={stickyNotificationDetails.refresh}
        backgroundColor={stickyNotificationDetails.backgroundColor}
      />


      {asideBarData && country &&
        <main id="my-account-page" className="account-page">
          {currentGlobalModalName != null && renderModal(currentGlobalModalName)}
          <HeaderCMS country={country} refreshData={refreshData}

          />

          <div id="my-account-page-container" className="account-container">
            <div id="my-account-page-layout" className="account-asidebar-and-content">
              <MyAccountAsideBar
                asideBarData={asideBarData}
                activeMenuLinkId={activeMenuLinkId}
                showAllLinks={asideMenuOpen}
                setShowAllLinks={setAsideMenuOpen}
                forceShowIconMenuLinkId={
                  storeWalkMeActive ? testLabStoreConfig.walkMe.targetMenuLinkId : null
                }
              />


              <Outlet
              context={{
                activeMenuLinkId,
                setActiveMenuLinkId,
                refreshData,
                setRefreshData,
                ordersProducts: asideBarData.ordersProducts,
                ordersStatuses: asideBarData.ordersStatuses,
                subscriptionCount: asideBarData.subscriptionCount,
                locale: asideBarData.locale,
                customer: {
                  id: asideBarData.id,
                  email: asideBarData.email,
                  hasPassword: asideBarData.hasPassword
                },
                //TODO STATE:
                country,
                setCurrentGlobalModalName,
                setStickyNotificationDetails,
                setError,
                setLoading,
                language: asideBarData.locale.language_id,
                currentGlobalModalName,
                activeOrdersCount: asideBarData.activeOrdersCount,
                asideBarData
              }}
              />
            </div>
          </div>

          <Footer
            showDiscountDisclaimer={activeMenuLinkId === "my-orders"}
            language={translator.getLanguageByCountry(country)}
            page="MyAccount" />

          {activeMenuLinkId !== testLabStoreConfig.menuLinkId && (
            <MyAccountStoreWalkMe
              language={asideBarData.locale.language_id}
              country={country}
              config={testLabStoreConfig}
              onRequestOpenMenu={openAsideMenu}
              onRequestCloseMenu={closeAsideMenu}
              onActiveChange={handleStoreWalkMeActiveChange}
            />
          )}
        </main>
      }
    </>
  );
};

export default MyAccountLayout;
